import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Save, Trash2, DollarSign } from "lucide-react";
import { Card, field, cfield, btn, fmtTs } from "./ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CustomersTab = ({ auth }) => {
  const [customers, setCustomers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/admin/customers`, auth).then((r) => setCustomers(r.data)).catch(() => {});
  useEffect(() => {
    load();
    axios.get(`${API}/leads`, auth).then((r) => setLeads(r.data.filter((l) => l.name || l.email))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickLead = (id) => {
    const l = leads.find((x) => x.id === id);
    if (!l) return;
    setForm({ name: l.name || "", email: l.email || "", phone: "" });
  };

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/customers`, form, auth);
      toast.success("Customer added.");
      setForm({ name: "", email: "", phone: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not add customer."); }
    finally { setBusy(false); }
  };

  const save = async (id, fields) => {
    try {
      await axios.patch(`${API}/admin/customers/${id}`, fields, auth);
      toast.success("Saved.");
      load();
    } catch { toast.error("Save failed."); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete ${name || "this customer"} permanently?`)) return;
    try {
      await axios.delete(`${API}/admin/customers/${id}`, auth);
      setCustomers((s) => s.filter((c) => c.id !== id));
    } catch { toast.error("Delete failed."); }
  };

  const draft = (id) => drafts[id] || {};

  return (
    <div className="space-y-8" data-testid="customers-tab">
      <Card title="Add Customer" color="bg-staxcyan" testid="customer-add-card">
        <select data-testid="customer-from-leads" defaultValue="" onChange={(e) => pickLead(e.target.value)} className={`${field} mb-3`}>
          <option value="" disabled>Start from an inquiry...</option>
          {leads.map((l) => (
            <option key={l.id} value={l.id}>{l.name || "Unnamed"}{l.email ? ` — ${l.email}` : ""}</option>
          ))}
        </select>
        <form onSubmit={add} className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <input data-testid="customer-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Name *" className={field} />
          <input data-testid="customer-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="Email" className={field} />
          <input data-testid="customer-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="Phone" className={field} />
          <button data-testid="customer-add" type="submit" disabled={busy} className={`${btn} bg-staxcyan text-ink`}>
            <Plus size={15} /> Add
          </button>
        </form>
      </Card>

      <Card title={`Customers (${customers.length})`} color="bg-staxmagenta" testid="customers-list">
        {customers.length === 0 && (
          <p className="font-mono2 text-xs uppercase tracking-widest text-ink/40">
            No customers yet — add one above or start from an inquiry.
          </p>
        )}
        <div className="space-y-3">
          {customers.map((c) => (
            <div key={c.id} data-testid={`customer-row-${c.id}`} className="border-2 border-ink/15 bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-display uppercase text-lg leading-tight">{c.name}</p>
                  <p className="text-sm text-ink/60 truncate">
                    {[c.email, c.phone].filter(Boolean).join(" · ") || "No contact info"}
                  </p>
                  <p className="mt-0.5 font-mono2 text-[10px] uppercase tracking-widest text-ink/35">
                    Since {fmtTs(c.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1.5 border-2 border-ink bg-staxyellow px-3 py-1.5">
                    <DollarSign size={13} />
                    <input
                      data-testid={`customer-paid-${c.id}`}
                      type="number"
                      min="0"
                      step="0.01"
                      value={draft(c.id).paid ?? c.paid ?? 0}
                      onChange={(e) => setDrafts((s) => ({ ...s, [c.id]: { ...draft(c.id), paid: e.target.value } }))}
                      className="w-24 bg-transparent font-display text-lg focus:outline-none"
                      aria-label="Total paid"
                    />
                  </span>
                  <button data-testid={`customer-paid-save-${c.id}`} onClick={() => save(c.id, { paid: parseFloat(draft(c.id).paid ?? c.paid ?? 0) })}
                    aria-label="Save paid amount" className={`${btn} bg-white text-ink`}>
                    <Save size={14} />
                  </button>
                  <button data-testid={`customer-delete-${c.id}`} onClick={() => del(c.id, c.name)} aria-label="Delete customer"
                    className="border-2 border-ink p-2 hover:bg-staxmagenta hover:text-white transition-colors">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <textarea
                  data-testid={`customer-notes-${c.id}`}
                  value={draft(c.id).notes ?? c.notes ?? ""}
                  onChange={(e) => setDrafts((s) => ({ ...s, [c.id]: { ...draft(c.id), notes: e.target.value } }))}
                  placeholder="Notes — projects, preferences, what they've paid for..."
                  className={`${cfield} min-h-[56px] resize-none`}
                />
                <button data-testid={`customer-notes-save-${c.id}`} onClick={() => save(c.id, { notes: draft(c.id).notes ?? c.notes ?? "" })}
                  aria-label="Save notes" className={`${btn} bg-staxcyan text-ink self-end`}>
                  <Save size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};
